import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

import ItemService from "../../../../services/customer_appCommonService";
import { InputField } from "../../../../Components/formElements/InputField";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";
import FileViewerModalForHB from "../../../../Components/FileViewer/FileViewerModalForHB";
import ServiceTallyStatusSection from "./ServiceTallyStatusSection";
import ServicePaymentSection from "./ServicePaymentSection";
import StockInConfirmModal from "./StockInConfirmModal";
import {
  SERVICE_ACTION_CALL_STATUS,
  SERVICE_STATUS,
  JOB_STATUS,
  ROLES,
  SERVICE_ACTION,
} from "./ServiceConstants";
import { hasRole } from "./serviceHelpers";
import FileUploadComponentForHB from "../../../../Components/formElements/FileUploadComponentForHB";

// ── Reusable Accordion Header ───────────────────────────────────────────────
const AccordionHeader = ({ iconLabel, title, subtitle, isOpen, onToggle }) => (
  <h2 className="accordion-header">
    <Row className="accordion-button" style={{ cursor: "default" }}>
      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
        <i
          className={`bi ${isOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
          onClick={onToggle}
          style={{ cursor: "pointer", fontSize: 16 }}
        />
      </Col>
      <Col xs={1} md={2} lg={2} className="text-center logoBtn">
        <span className="iconLogo">{iconLabel}</span>
      </Col>
      <Col xs={12} md={8} lg={8}>
        <span className="headerTitle">{title}</span>
        {subtitle && <span className="subHeaderTitle ms-2">{subtitle}</span>}
      </Col>
    </Row>
  </h2>
);

const ViewServiceDetailsModal = ({ show, onHide, data, onSuccess }) => {
  const userData = useSelector((state) => state?.user);
  const roles = userData?.data?.roles || [];

  // Use same role keys as ServiceDetailPage for consistency
  const isStore = hasRole(roles, ROLES.STORE);
  const isSE = hasRole(roles, ROLES.SERVICE_EXECUTIVE);

  const currentUser = {
    userId: userData?.data?.userId,
    name: userData?.data?.firstName || userData?.data?.name || "",
  };

  // ── State ──────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [serviceRecord, setServiceRecord] = useState(null);
  const [newBarcodeMap, setNewBarcodeMap] = useState({});
  const [returnedBarcodeMap, setReturnedBarcodeMap] = useState({});
  const [stockInModal, setStockInModal] = useState(null);
  const [completionAttachmentId, setCompletionAttachmentId] = useState("");
  const [fileViewModal, setFileViewModal] = useState({ show: false, id: null });

  const [seUpdateData, setSeUpdateData] = useState({
    completionStatus: SERVICE_STATUS.IN_PROGRESS,
    completionNotes: "",
    completionDate: "",
  });

  // Accordion toggles
  const [isPatientOpen, setIsPatientOpen] = useState(true);
  const [isItemsOpen, setIsItemsOpen] = useState(true);
  const [isSeListOpen, setIsSeListOpen] = useState(true);
  const [isMoneyOpen, setIsMoneyOpen] = useState(true);
  const [isUpdateOpen, setIsUpdateOpen] = useState(true);

  const isExtensionService = serviceRecord?.activeDocumentType === "Extension";
  const activeDocNr =
    serviceRecord?.activeDocumentNr || serviceRecord?.equipmentRentNr;

  // ── Reset state on open ────────────────────────────────────────────────────
  useEffect(() => {
    if (show) {
      setServiceRecord(null);
      setCompletionAttachmentId("");
      setNewBarcodeMap({});
      setReturnedBarcodeMap({});
      setSeUpdateData({
        completionStatus: SERVICE_STATUS.IN_PROGRESS,
        completionNotes: "",
        completionDate: "",
      });

      if (data?.latestServiceNr) {
        fetchServiceRecord(data.latestServiceNr);
      } else if (data?.documentNumber) {
        fetchLatestByErf(data.documentNumber);
      }
    }
  }, [show, data?.latestServiceNr, data?.documentNumber]);

  // ── Fetch service record ───────────────────────────────────────────────────
  const fetchServiceRecord = async (serviceNr) => {
    if (!serviceNr) return;
    setLoading(true);
    try {
      const res = await ItemService.generateQuery({
        queryName: "ERF_Service_Detail",
        documentNumber: serviceNr,
        page: 1,
        per_page: 1,
        timeZone: "Asia/Calcutta",
      });
      const record = res?.data?.[0] || res?.data?.data?.[0];
      if (record) setServiceRecord(record);
      else toast.error("Service record not found");
    } catch {
      toast.error("Failed to load service record");
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestByErf = async (erfNr) => {
    if (!erfNr) return;
    setLoading(true);
    try {
      const res = await ItemService.generateQuery({
        queryName: "ERF_Service_Detail",
        equipmentRentNr: erfNr,
        sort: { createdAt: -1 },
        page: 1,
        per_page: 1,
        timeZone: "Asia/Calcutta",
      });
      const record = res?.data?.[0] || res?.data?.data?.[0];
      if (record) setServiceRecord(record);
    } catch {
      // silent fallback
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const fetchMaterialRecord = async (barcode) => {
    const res = await ItemService.generateQuery({
      queryName: "MaterilBarcodeList",
      rowFilter: { $and: [{ barcode }] },
      timeZone: "Asia/Calcutta",
    });
    return res?.status === 200 && res.data?.length > 0 ? res.data[0] : null;
  };

  // Validates barcode is in stock for the correct godown and material
  const validateReplacementBarcode = async (
    barcode,
    materialCode,
    itemType,
  ) => {
    const godownCode =
      itemType === "Sale" || itemType === "Service" ? "SL" : "RE";
    try {
      const res = await ItemService.generateQuery({
        queryName: "MaterilBarcodeList",
        page: 1,
        per_page: 1,
        rowFilter: {
          $and: [
            {
              godownCode,
              materialStatus: "StockIn",
              barcode,
              materialCode,
            },
          ],
        },
        timeZone: "Asia/Calcutta",
      });
      return (
        res?.status === 200 && Array.isArray(res.data) && res.data.length > 0
      );
    } catch {
      return false;
    }
  };

  // Updates ONLY the barcode field on the matching ERF item
  // Uses equipmentItemNr + materialCode to identify the exact item
  // Safe when same materialCode exists in both rentItems and saleItems
  const updateErfItemBarcode = async (item, newBarcode) => {
    try {
      const erfRes = await ItemService.generateQuery({
        queryName: isExtensionService ? "ERFExtDetail" : "ERFDetail",
        documentNumber: activeDocNr,
        page: 1,
        per_page: 1,
        timeZone: "Asia/Calcutta",
      });

      const erfRecord = erfRes?.data?.[0] || erfRes?.data?.data?.[0];
      if (!erfRecord) {
        toast.error("ERF record not found — barcode not synced to ERF");
        return;
      }

      const arrayKey = item.itemType === "Sale" ? "saleItems" : "rentItems";
      const erfItems = erfRecord[arrayKey] || [];

      let matchedCount = 0;
      const updatedErfItems = erfItems.map((erfItem) => {
        const isMatch =
          String(erfItem.equipmentItemNr) === String(item.equipmentItemNr) &&
          erfItem.materialCode === item.materialCode;

        if (isMatch && matchedCount === 0) {
          matchedCount++;
          return { ...erfItem, barcode: newBarcode }; // ONLY barcode updated
        }
        return erfItem;
      });

      if (matchedCount === 0) {
        toast.error(
          `ERF item not found (equipmentItemNr: ${item.equipmentItemNr}, ` +
            `materialCode: ${item.materialCode}) — barcode not synced`,
        );
        return;
      }

      const barcodePayload = {
        documentNumber: activeDocNr,
        equipmentRentNr: activeDocNr,
        [arrayKey]: updatedErfItems,
      };
      if (isExtensionService) {
        await ItemService.updateExtendEquipmentRent(barcodePayload);
      } else {
        await ItemService.updateEquipmentRentForm(barcodePayload);
      }
    } catch (err) {
      console.error("updateErfItemBarcode error:", err);
      toast.error("Replacement assigned but ERF barcode sync failed");
    }
  };

  // ── STORE: assign replacement barcode ─────────────────────────────────────
  const handleAssignBarcode = async (item) => {
    const newBarcode = (newBarcodeMap[item.lineItemId] || "").trim();
    if (!newBarcode) return toast.error("Enter replacement barcode");

    setLoading(true);
    try {
      const now = new Date().toISOString();

      // Step 1: Validate barcode is in stock with matching material code
      const isValid = await validateReplacementBarcode(
        newBarcode,
        item.materialCode,
        item.itemType,
      );
      if (!isValid) {
        toast.error(
          `Barcode "${newBarcode}" is not in stock or does not match ` +
            `material code "${item.materialCode}"`,
        );
        return;
      }

      // Step 2: Get material master record for history
      const matRecord = await fetchMaterialRecord(newBarcode);
      if (!matRecord) {
        toast.error("Material master record not found for this barcode");
        return;
      }

      // Step 3: Push Stock Out history onto the new barcode
      const historyEntry = {
        status: "Stock Out",
        equipmentRentNr: serviceRecord.equipmentRentNr,
        equipmentItemNr: item.equipmentItemNr,
        erfTypeCode: serviceRecord.erfTypeCode || "RE",
        serviceAction: SERVICE_ACTION.REPLACEMENT,
        serviceNr: serviceRecord.erfServiceNumber,
        condition: "Good",
        updatedAt: now,
        updatedBy: currentUser.userId,
        updatedByName: currentUser.name,
      };

      await ItemService.updateMaterialBarcodeItem({
        barcode: newBarcode,
        documentNumber: matRecord.documentNumber,
        materialStatus: "StockOut",
        barCode: "Assigned",
        materialCode: item.materialCode,
        barcodeERFHistory: [
          ...(matRecord.barcodeERFHistory || []),
          historyEntry,
        ],
      });

      // Step 4: Update only barcode assignment fields on service item
      const updatedItems = serviceRecord.serviceRequiredItems.map((si) =>
        si.lineItemId === item.lineItemId
          ? {
              ...si,
              newBarcode,
              barcodeAssigned: true,
              barcodeAssignedAt: now,
              barcodeAssignedBy: currentUser.userId,
              // No other fields changed
            }
          : si,
      );

      await ItemService.updateErfService({
        erfServiceNumber: serviceRecord.erfServiceNumber,
        serviceRequiredItems: updatedItems,
      });

      // Step 5: Sync the new barcode to the ERF item (only barcode field)
      await updateErfItemBarcode(item, newBarcode);

      toast.success(
        `Replacement barcode "${newBarcode}" assigned and ERF updated`,
      );
      setNewBarcodeMap((p) => ({ ...p, [item.lineItemId]: "" }));
      await fetchServiceRecord(serviceRecord.erfServiceNumber);
      onSuccess?.();
    } catch (err) {
      console.error("handleAssignBarcode error:", err);
      toast.error("Failed to assign replacement barcode");
    } finally {
      setLoading(false);
    }
  };

  // ── STORE: confirm all replacement barcodes assigned ──────────────────────
  const handleConfirmAllBarcodes = async () => {
    setLoading(true);
    try {
      await ItemService.updateErfService({
        erfServiceNumber: serviceRecord.erfServiceNumber,
        storeBarcodesConfirmed: true,
      });
      const confirmPayload = {
        documentNumber: activeDocNr,
        equipmentRentNr: activeDocNr,
        serviceAction: SERVICE_ACTION_CALL_STATUS.SERVICE_ASSIGNED,
      };
      if (isExtensionService) {
        await ItemService.updateExtendEquipmentRent(confirmPayload);
      } else {
        await ItemService.updateEquipmentRentForm(confirmPayload);
      }
      toast.success("All barcodes confirmed — SE can now proceed");
      await fetchServiceRecord(serviceRecord.erfServiceNumber);
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error("Failed to confirm barcodes");
    } finally {
      setLoading(false);
    }
  };

  // ── SE: collect old barcode from patient ──────────────────────────────────
  // const handleReturnBarcode = async (item) => {
  //   const returned = (returnedBarcodeMap[item.lineItemId] || "").trim();
  //   if (!returned) return toast.error("Enter collected barcode");

  //   // Check 1: Already fully stocked in by Store
  //   if (item.returnedToStore === true) {
  //     toast.error(
  //       `This item has already been stocked in by Store. No action needed.`,
  //     );
  //     return;
  //   }

  //   // Check 2: SE already recorded collection, awaiting Store stock-in
  //   if (item.returnedBarcode && item.returnedToStore === false) {
  //     toast.error(
  //       `Barcode "${item.returnedBarcode}" is already recorded as collected ` +
  //         `and is awaiting Store stock-in.`,
  //     );
  //     return;
  //   }

  //   // Check 3: Entered barcode must match the item's current barcode
  //   const expectedBarcode = (item.currentBarcode || "").trim();
  //   if (!expectedBarcode) {
  //     toast.error("No current barcode on this service item. Contact admin.");
  //     return;
  //   }

  //   if (returned !== expectedBarcode) {
  //     toast.error(
  //       `Mismatch! Expected "${expectedBarcode}" for ${item.materialCode} ` +
  //         `but got "${returned}". Please scan the correct old item.`,
  //     );
  //     setReturnedBarcodeMap((p) => ({ ...p, [item.lineItemId]: "" }));
  //     return;
  //   }

  //   setLoading(true);
  //   try {
  //     const updatedItems = serviceRecord.serviceRequiredItems.map((si) =>
  //       si.lineItemId === item.lineItemId
  //         ? {
  //             ...si,
  //             returnedBarcode: returned,
  //             returnedToStore: false,
  //             collectedFromPatientAt: new Date().toISOString(),
  //             collectedFromPatientBy: currentUser.userId,
  //             collectedFromPatientByName: currentUser.name,
  //           }
  //         : si,
  //     );

  //     await ItemService.updateErfService({
  //       erfServiceNumber: serviceRecord.erfServiceNumber,
  //       serviceRequiredItems: updatedItems,
  //     });

  //     toast.success(
  //       `"${returned}" confirmed as collected. Awaiting Store stock-in.`,
  //     );
  //     setReturnedBarcodeMap((p) => ({ ...p, [item.lineItemId]: "" }));
  //     await fetchServiceRecord(serviceRecord.erfServiceNumber);
  //     onSuccess?.();
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Failed to mark collection");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // ── SE: collect old barcode from patient ──────────────────────────────────
  const handleReturnBarcode = async (item) => {
    const returned = (returnedBarcodeMap[item.lineItemId] || "").trim();
    if (!returned) return toast.error("Enter collected barcode");

    // Check 1: Already fully stocked in by Store
    if (item.returnedToStore === true) {
      toast.error(
        `This item has already been stocked in by Store. No action needed.`,
      );
      return;
    }

    // Check 2: SE already recorded collection, awaiting Store stock-in
    if (item.returnedBarcode && item.returnedToStore === false) {
      toast.error(
        `Barcode "${item.returnedBarcode}" is already recorded as collected ` +
          `and is awaiting Store stock-in.`,
      );
      return;
    }

    // Check 3: Entered barcode must match the item's current barcode
    const expectedBarcode = (item.currentBarcode || "").trim();
    if (!expectedBarcode) {
      toast.error("No current barcode on this service item. Contact admin.");
      return;
    }

    if (returned !== expectedBarcode) {
      toast.error(
        `Mismatch! Expected "${expectedBarcode}" for ${item.materialCode} ` +
          `but got "${returned}". Please scan the correct old item.`,
      );
      setReturnedBarcodeMap((p) => ({ ...p, [item.lineItemId]: "" }));
      return;
    }

    setLoading(true);
    try {
      // CRITICAL: fetch fresh record from DB before mapping.
      // Without this, two rapid Confirm clicks on different items use stale
      // React state and the second click overwrites the first item's barcode
      // back to empty.
      const freshRes = await ItemService.generateQuery({
        queryName: "ERF_Service_Detail",
        documentNumber: serviceRecord.erfServiceNumber,
        page: 1,
        per_page: 1,
        timeZone: "Asia/Calcutta",
      });
      const freshRecord = freshRes?.data?.[0] || freshRes?.data?.data?.[0];
      if (!freshRecord) {
        throw new Error(
          "Could not load latest service record. Please refresh and try again.",
        );
      }

      // Map over FRESH DB items — never over React state
      const updatedItems = freshRecord.serviceRequiredItems.map(
        (si) =>
          si.lineItemId === item.lineItemId
            ? {
                ...si,
                returnedBarcode: returned,
                returnedToStore: false,
                collectedFromPatientAt: new Date().toISOString(),
                collectedFromPatientBy: currentUser.userId,
                collectedFromPatientByName: currentUser.name,
              }
            : si, // preserve other items exactly from DB
      );

      await ItemService.updateErfService({
        erfServiceNumber: freshRecord.erfServiceNumber,
        serviceRequiredItems: updatedItems,
      });

      toast.success(
        `"${returned}" confirmed as collected. Awaiting Store stock-in.`,
      );
      setReturnedBarcodeMap((p) => ({ ...p, [item.lineItemId]: "" }));
      await fetchServiceRecord(freshRecord.erfServiceNumber);
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to mark collection");
    } finally {
      setLoading(false);
    }
  };

  // ── STORE: open stock-in modal ─────────────────────────────────────────────
  const openStockInModal = (item) => setStockInModal({ item });

  // ── STORE: confirm stock-in ────────────────────────────────────────────────
  const handleStockInConfirm = async ({ condition, qualityCheck, remarks }) => {
    const { item } = stockInModal;
    setStockInModal(null);
    setLoading(true);

    try {
      const now = new Date().toISOString();

      // CRITICAL: Fetch fresh record from DB to avoid stale closure bug.
      // If item 1 was stocked in earlier, React state may still show the
      // old snapshot — using that would reset item 1 back to false when
      // item 2 is stocked in. Always use DB state for the map operation.
      const freshRes = await ItemService.generateQuery({
        queryName: "ERF_Service_Detail",
        documentNumber: serviceRecord.erfServiceNumber,
        page: 1,
        per_page: 1,
        timeZone: "Asia/Calcutta",
      });

      const freshRecord = freshRes?.data?.[0] || freshRes?.data?.data?.[0];
      if (!freshRecord) {
        throw new Error(
          "Could not load latest service record. Please refresh and try again.",
        );
      }

      // Validate old barcode has a material master record
      const matRecord = await fetchMaterialRecord(item.returnedBarcode);
      if (!matRecord) {
        throw new Error(
          `No material record found for barcode "${item.returnedBarcode}". Contact admin.`,
        );
      }

      // Push Stock-In history onto the old (returned) barcode
      const historyEntry = {
        status: "Stock In",
        equipmentRentNr: freshRecord.equipmentRentNr,
        equipmentItemNr: item.equipmentItemNr,
        erfTypeCode: freshRecord.erfTypeCode || "RE",
        serviceAction: SERVICE_ACTION.REPLACEMENT,
        serviceNr: freshRecord.erfServiceNumber,
        condition,
        remarks,
        qualityCheck,
        updatedAt: now,
        updatedBy: currentUser.userId,
        updatedByName: currentUser.name,
      };

      await ItemService.updateMaterialBarcodeItem({
        barcode: item.returnedBarcode,
        documentNumber: matRecord.documentNumber,
        materialStatus: "StockIn",
        barCode: "StockIn",
        barcodeERFHistory: [
          ...(matRecord.barcodeERFHistory || []),
          historyEntry,
        ],
      });

      // Map over FRESH DB items — never over React state
      // This is the fix for the stale closure bug
      const updatedItems = freshRecord.serviceRequiredItems.map(
        (si) =>
          si.lineItemId === item.lineItemId
            ? {
                ...si,
                returnedToStore: true,
                replacementComplete: true,
                stockInAt: now,
                stockInBy: currentUser.userId,
                stockInCondition: condition,
                qualityCheck,
                stockInRemarks: remarks,
              }
            : si, // all other items preserved exactly from DB
      );

      // Update ONLY serviceRequiredItems.
      // serviceStatus and jobDoneStatus are exclusively controlled by SE.
      // Stock-in is a Store-only physical operation — never touches status.
      await ItemService.updateErfService({
        erfServiceNumber: freshRecord.erfServiceNumber,
        serviceRequiredItems: updatedItems,
      });

      // Progress feedback
      const allReplacement = updatedItems.filter(
        (r) => r.serviceAction === SERVICE_ACTION.REPLACEMENT,
      );
      const doneCount = allReplacement.filter((r) => r.returnedToStore).length;

      toast.success(
        `Stock-in done for "${item.materialCode}" (${item.returnedBarcode}). ` +
          `${doneCount}/${allReplacement.length} replacement(s) stocked in.`,
      );

      await fetchServiceRecord(freshRecord.erfServiceNumber);
      onSuccess?.();
    } catch (err) {
      console.error("handleStockInConfirm error:", err);
      toast.error(err.message || "Stock-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── SE: update completion status ──────────────────────────────────────────
  const handleSeCompletionUpdate = async () => {
    if (!serviceRecord?.erfServiceNumber)
      return toast.error("No service record loaded");

    if (!seUpdateData.completionNotes.trim())
      return toast.error("Enter completion notes");

    const isCompleted =
      seUpdateData.completionStatus === SERVICE_STATUS.COMPLETED;

    if (isCompleted && !completionAttachmentId)
      return toast.error(
        "Upload completion document before marking as Completed",
      );

    setLoading(true);
    try {
      // SE exclusively controls serviceStatus and jobDoneStatus
      await ItemService.updateErfService({
        erfServiceNumber: serviceRecord.erfServiceNumber,
        serviceStatus: seUpdateData.completionStatus,
        completedAt: isCompleted ? new Date().toISOString() : null,
        completedBy: isCompleted ? currentUser.userId : null,
        completedByName: isCompleted ? currentUser.name : null,
        completionNotes: seUpdateData.completionNotes,
        completionDate: seUpdateData.completionDate || null,
        completionAttachmentId: completionAttachmentId || null,
        jobDoneStatus: isCompleted ? JOB_STATUS.DONE : JOB_STATUS.PENDING,
      });

      const completionPayload = {
        documentNumber: activeDocNr,
        equipmentRentNr: activeDocNr,
        serviceAction: isCompleted
          ? SERVICE_ACTION_CALL_STATUS.SERVICE_COMPLETED
          : SERVICE_ACTION_CALL_STATUS.SERVICE_IN_PROGRESS,
      };
      if (isExtensionService) {
        await ItemService.updateExtendEquipmentRent(completionPayload);
      } else {
        await ItemService.updateEquipmentRentForm(completionPayload);
      }

      if (isCompleted) {
        toast.success("Service marked as Completed.");
        // Refresh parent list then close modal
        onSuccess?.();
        onHide();
      } else {
        toast.success("Service status updated.");
        await fetchServiceRecord(serviceRecord.erfServiceNumber);
        onSuccess?.();
      }
    } catch (err) {
      console.error("handleSeCompletionUpdate error:", err);
      toast.error("Failed to update service status.");
    } finally {
      setLoading(false);
    }
  };

  if (!data) return null;

  // ── Computed values ────────────────────────────────────────────────────────
  const requiredItems = serviceRecord?.serviceRequiredItems || [];
  const seList = serviceRecord?.assignedServiceExecutives || [];
  const replacementItems = requiredItems.filter(
    (r) => r.serviceAction === SERVICE_ACTION.REPLACEMENT,
  );
  const hasReplacementItems = replacementItems.length > 0;
  const allReplacementBarcodesAssigned =
    hasReplacementItems &&
    replacementItems.every((r) => r.barcodeAssigned === true);
  const storeAlreadyConfirmed = serviceRecord?.storeBarcodesConfirmed === true;
  const seCanAct = !hasReplacementItems || storeAlreadyConfirmed;
  const isServiceCompleted =
    serviceRecord?.serviceStatus === SERVICE_STATUS.COMPLETED;

  // ── Item table columns ─────────────────────────────────────────────────────
  const itemColumns = [
    {
      name: "#",
      cell: (_, i) => i + 1,
      width: "50px",
    },
    {
      name: "Material",
      width: "200px",
      cell: (r) => (
        <div className="p-1">
          <div className="fw-bold">{r.materialCode}</div>
          <div className="text-muted small">{r.materialDescription}</div>
        </div>
      ),
    },
    {
      name: "Action",
      width: "120px",
      cell: (r) => (
        <span
          className={`badge ${
            r.serviceAction === SERVICE_ACTION.REPLACEMENT
              ? "bg-warning text-dark"
              : "bg-info text-dark"
          }`}
        >
          {r.serviceAction || "--"}
        </span>
      ),
    },
    {
      name: "Current Barcode",
      selector: (r) => r.currentBarcode || "--",
      width: "150px",
    },
    {
      name: "New Barcode",
      width: "260px",
      cell: (r) => {
        if (r.serviceAction !== SERVICE_ACTION.REPLACEMENT) return "—";

        if (r.barcodeAssigned)
          return <b className="text-success">{r.newBarcode}</b>;

        if (!isStore)
          return <span className="text-muted small">Awaiting Store</span>;

        return (
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Scan/enter new barcode"
              value={newBarcodeMap[r.lineItemId] || ""}
              onChange={(e) =>
                setNewBarcodeMap((p) => ({
                  ...p,
                  [r.lineItemId]: e.target.value,
                }))
              }
            />
            <button
              className="btnTable btn-sm"
              disabled={loading}
              onClick={() => handleAssignBarcode(r)}
            >
              Assign
            </button>
          </div>
        );
      },
    },
    {
      name: "Returned Barcode",
      width: "280px",
      cell: (r) => {
        if (r.serviceAction !== SERVICE_ACTION.REPLACEMENT) return "—";

        if (!r.barcodeAssigned)
          return <span className="text-muted small">--</span>;

        // Fully stocked in — show summary
        if (r.returnedToStore) {
          return (
            <div className="py-1">
              <div className="text-success fw-bold font-monospace">
                {r.returnedBarcode}
                <i className="bi bi-check-circle-fill ms-1" />
              </div>
              <small className="text-muted d-block">
                {r.stockInCondition && `Condition: ${r.stockInCondition}`}
                {r.qualityCheck && ` · QC: ${r.qualityCheck}`}
              </small>
              <small className="text-muted d-block">
                {r.stockInAt &&
                  `Stocked in: ${new Date(r.stockInAt).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`}
              </small>
            </div>
          );
        }

        // SE collected, awaiting Store stock-in
        if (r.returnedBarcode && !r.returnedToStore) {
          return isStore ? (
            <button
              className="btn btn-sm btn-outline-success"
              onClick={() => openStockInModal(r)}
            >
              <i className="bi bi-box-arrow-in-down me-1" />
              Stock In ({r.returnedBarcode})
            </button>
          ) : (
            <span className="text-warning small">
              {r.returnedBarcode} — awaiting Store
            </span>
          );
        }

        // SE needs to collect — show input with expected barcode hint
        if (!isSE) return <span className="text-muted small">Awaiting SE</span>;

        return (
          <div className="d-flex flex-column gap-1 w-100">
            <small className="text-muted">
              Expected:{" "}
              <span className="fw-bold text-dark font-monospace">
                {r.currentBarcode || "—"}
              </span>
            </small>
            <div className="d-flex gap-2">
              <input
                type="text"
                className={`form-control form-control-sm font-monospace ${
                  returnedBarcodeMap[r.lineItemId] &&
                  returnedBarcodeMap[r.lineItemId].trim() !== r.currentBarcode
                    ? "is-invalid"
                    : returnedBarcodeMap[r.lineItemId] &&
                        returnedBarcodeMap[r.lineItemId].trim() ===
                          r.currentBarcode
                      ? "is-valid"
                      : ""
                }`}
                placeholder={`Scan old barcode`}
                value={returnedBarcodeMap[r.lineItemId] || ""}
                onChange={(e) =>
                  setReturnedBarcodeMap((p) => ({
                    ...p,
                    [r.lineItemId]: e.target.value,
                  }))
                }
              />
              <button
                className="btnTable btn-sm"
                disabled={loading}
                onClick={() => handleReturnBarcode(r)}
              >
                Confirm
              </button>
            </div>
            {returnedBarcodeMap[r.lineItemId] &&
              returnedBarcodeMap[r.lineItemId].trim() !== r.currentBarcode && (
                <small className="text-danger">
                  <i className="bi bi-exclamation-triangle me-1" />
                  Does not match expected barcode
                </small>
              )}
            {returnedBarcodeMap[r.lineItemId] &&
              returnedBarcodeMap[r.lineItemId].trim() === r.currentBarcode && (
                <small className="text-success">
                  <i className="bi bi-check-circle me-1" />
                  Barcode matches
                </small>
              )}
          </div>
        );
      },
    },
  ];

  // ── SE assignment history columns ──────────────────────────────────────────
  const seColumns = [
    {
      name: "SE Code",
      selector: (r) => r.serviceProviderCode || "--",
      width: "110px",
    },
    {
      name: "SE Name",
      cell: (r) => <b>{r.serviceProviderName || "--"}</b>,
      width: "150px",
    },
    {
      name: "Assigned At",
      cell: (r) =>
        r.assignDateAndTime
          ? new Date(r.assignDateAndTime).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "--",
      width: "170px",
    },
    {
      name: "Category",
      selector: (r) => r.serviceAssignCategory || "--",
      width: "130px",
    },
    {
      name: "Description",
      selector: (r) => r.serviceAssignCategoryDescription || "--",
      width: "200px",
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
        {/* ── Modal Header with service status badge ── */}
        <Modal.Header closeButton>
          <Modal.Title>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <i className="bi bi-tools fs-4" />
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span>Service Details</span>
                  {serviceRecord?.serviceStatus && (
                    <span
                      className={`badge ${
                        isServiceCompleted
                          ? "bg-success"
                          : serviceRecord.serviceStatus ===
                              SERVICE_STATUS.CANCELLED
                            ? "bg-danger"
                            : "bg-warning text-dark"
                      }`}
                      style={{ fontSize: "0.7rem" }}
                    >
                      {serviceRecord.serviceStatus}
                    </span>
                  )}
                </div>
                {serviceRecord?.erfServiceNumber && (
                  <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                    {serviceRecord.erfServiceNumber}
                    {isServiceCompleted && serviceRecord.completedByName && (
                      <span className="text-success ms-2">
                        <i className="bi bi-check-circle-fill me-1" />
                        Completed by {serviceRecord.completedByName}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            maxHeight: "80vh",
            overflowY: "auto",
            padding: "16px",
          }}
        >
          {loading && !serviceRecord ? (
            <div className="p-4 text-center text-muted">
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              />
              Loading service record...
            </div>
          ) : !serviceRecord ? (
            <div className="p-4 text-center text-muted">
              No service record found.
            </div>
          ) : (
            <>
              {/* ACC 1 — Patient / Service Details */}
              <div
                className="accordion mt-1"
                style={{
                  borderRadius: 10,
                  overflow: "hidden",
                  marginBottom: 12,
                }}
              >
                <div className="accordion-item">
                  <AccordionHeader
                    iconLabel="SR"
                    title="Service / Patient Details"
                    subtitle="Patient & Caregiver Information"
                    isOpen={isPatientOpen}
                    onToggle={() => setIsPatientOpen((p) => !p)}
                  />
                  {isPatientOpen && (
                    <div className="accordion-body">
                      <Row className="mb-3">
                        {[
                          {
                            label: "ERF No",
                            value: serviceRecord.equipmentRentNr,
                          },
                          {
                            label: "Service Date",
                            value: serviceRecord.serviceDate,
                          },
                          {
                            label: "Service Time",
                            value: serviceRecord.serviceTime,
                          },
                          {
                            label: "Sale Point",
                            value: serviceRecord.salePoint,
                          },
                          {
                            label: "Patient Name",
                            value: serviceRecord.patientName,
                          },
                          {
                            label: "Age",
                            value: serviceRecord.age,
                          },
                          {
                            label: "Patient No",
                            value: serviceRecord.mobileNr,
                          },
                          {
                            label: "Email",
                            value: serviceRecord.email,
                          },
                          {
                            label: "ERF Type",
                            value: serviceRecord.erfType,
                          },
                          {
                            label: "ERF Type Code",
                            value: serviceRecord.erfTypeCode,
                          },
                          {
                            label: "Cost Centre",
                            value: serviceRecord.costCenter,
                          },
                          {
                            label: "Caregiver Name",
                            value: serviceRecord.caregiverName,
                          },
                          {
                            label: "Relationship",
                            value: serviceRecord.caregiverRelation,
                          },
                          {
                            label: "Caregiver Mobile",
                            value: serviceRecord.caregiverMobileNr,
                          },
                          {
                            label: "Service Status",
                            value: serviceRecord.serviceStatus,
                          },
                        ].map(({ label, value }) => (
                          <Col
                            key={label}
                            lg={3}
                            md={6}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label={label}
                              value={value || ""}
                              disabled
                            />
                          </Col>
                        ))}
                      </Row>

                      {isServiceCompleted && (
                        <Row className="mb-3">
                          {[
                            {
                              label: "Completed By",
                              value: serviceRecord.completedByName,
                            },
                            {
                              label: "Completed At",
                              value: serviceRecord.completedAt
                                ? new Date(
                                    serviceRecord.completedAt,
                                  ).toLocaleString("en-IN")
                                : "",
                            },
                            {
                              label: "Completion Notes",
                              value: serviceRecord.completionNotes,
                            },
                          ].map(({ label, value }) => (
                            <Col
                              key={label}
                              lg={4}
                              md={6}
                              className="commTopButtonRightLeftPadding"
                            >
                              <InputField
                                className="inputBox"
                                label={label}
                                value={value || ""}
                                disabled
                              />
                            </Col>
                          ))}
                        </Row>
                      )}

                      <hr className="my-3" />
                      <Row className="mb-3">
                        {[
                          {
                            label: "Delivery Address",
                            value: serviceRecord.deliveryAddress,
                            cols: 6,
                          },
                          {
                            label: "Billing Address",
                            value: serviceRecord.billingAddress,
                            cols: 6,
                          },
                          {
                            label: "Challenges",
                            value: serviceRecord.challenges,
                            cols: 12,
                          },
                        ].map(({ label, value, cols }) => (
                          <Col
                            key={label}
                            lg={cols}
                            md={12}
                            className="commTopButtonRightLeftPadding mt-2"
                          >
                            <TextAreaField
                              className="inputBox"
                              label={label}
                              value={value || ""}
                              disabled
                              rows={2}
                            />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </div>
              </div>

              {/* ACC 2 — Service Required Items */}
              <div
                className="accordion"
                style={{
                  borderRadius: 10,
                  overflow: "hidden",
                  marginBottom: 12,
                }}
              >
                <div className="accordion-item">
                  <AccordionHeader
                    iconLabel="SI"
                    title="Service Required Items"
                    subtitle={`${requiredItems.length} item(s)`}
                    isOpen={isItemsOpen}
                    onToggle={() => setIsItemsOpen((p) => !p)}
                  />
                  {isItemsOpen && (
                    <div className="accordion-body p-0">
                      {requiredItems.length === 0 ? (
                        <div className="p-4 text-center text-muted">
                          <i className="bi bi-inbox fs-4 d-block mb-2" />
                          No service items
                        </div>
                      ) : (
                        <DataTable
                          columns={itemColumns}
                          data={requiredItems}
                          striped
                          pagination
                        />
                      )}

                      {/* Store: confirm all replacement barcodes assigned */}
                      {isStore &&
                        hasReplacementItems &&
                        !storeAlreadyConfirmed && (
                          <div className="p-3 border-top">
                            <Row className="align-items-center">
                              <Col
                                lg={8}
                                md={8}
                                className="commTopButtonRightLeftPadding"
                              >
                                {allReplacementBarcodesAssigned ? (
                                  <span className="text-success fw-semibold">
                                    <i className="bi bi-check-circle me-1" />
                                    All replacement barcodes assigned — confirm
                                    to release to SE
                                  </span>
                                ) : (
                                  <span className="text-warning fw-semibold">
                                    <i className="bi bi-exclamation-circle me-1" />
                                    Assign barcodes for all{" "}
                                    {replacementItems.length} replacement
                                    item(s) before confirming
                                  </span>
                                )}
                              </Col>
                              <Col
                                lg={4}
                                md={4}
                                className="commTopButtonRightLeftPadding text-end"
                              >
                                <button
                                  className="btnTable"
                                  disabled={
                                    loading || !allReplacementBarcodesAssigned
                                  }
                                  onClick={handleConfirmAllBarcodes}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <i className="bi bi-check2-all" />
                                  Confirm All Barcodes Assigned
                                </button>
                              </Col>
                            </Row>
                          </div>
                        )}

                      {isStore &&
                        hasReplacementItems &&
                        storeAlreadyConfirmed && (
                          <div className="p-3 border-top">
                            <span className="text-success fw-semibold">
                              <i className="bi bi-check2-all me-1" />
                              Store confirmed — SE can proceed
                            </span>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>

              {/* ACC 3 — Assigned SE History */}
              <div
                className="accordion"
                style={{
                  borderRadius: 10,
                  overflow: "hidden",
                  marginBottom: 12,
                }}
              >
                <div className="accordion-item">
                  <AccordionHeader
                    iconLabel="SE"
                    title="Assigned Service Executives"
                    subtitle={`${seList.length} assignment(s)`}
                    isOpen={isSeListOpen}
                    onToggle={() => setIsSeListOpen((p) => !p)}
                  />
                  {isSeListOpen && (
                    <div className="accordion-body p-0">
                      {seList.length === 0 ? (
                        <div className="p-4 text-center text-muted">
                          No SE assigned
                        </div>
                      ) : (
                        <DataTable columns={seColumns} data={seList} striped />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ACC 4 — Money Collection */}
              {serviceRecord.shouldCollectMoney && (
                <div
                  className="accordion"
                  style={{
                    borderRadius: 10,
                    overflow: "hidden",
                    marginBottom: 12,
                  }}
                >
                  <div className="accordion-item">
                    <AccordionHeader
                      iconLabel="MC"
                      title="Money Collection"
                      subtitle={`₹ ${(
                        serviceRecord.amountToCollect || 0
                      ).toFixed(2)}`}
                      isOpen={isMoneyOpen}
                      onToggle={() => setIsMoneyOpen((p) => !p)}
                    />
                    {/* {isMoneyOpen && (
                      <div className="accordion-body">
                        <ServicePaymentSection
                          serviceRecord={serviceRecord}
                          onRefresh={() =>
                            fetchServiceRecord(serviceRecord.erfServiceNumber)
                          }
                        />
                      </div>
                    )} */}
                    {isMoneyOpen && (
                      <div className="accordion-body">
                        {/* Service Charges Breakdown */}
                        <Row className="mb-3 pb-3 border-bottom">
                          <Col
                            lg={3}
                            md={6}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Net Amount (excl. GST)"
                              value={Number(
                                serviceRecord?.serviceNetTotal || 0,
                              ).toFixed(2)}
                              disabled
                            />
                          </Col>
                          <Col
                            lg={2}
                            md={6}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="GST %"
                              value={serviceRecord?.taxPercentage ?? 18}
                              disabled
                            />
                          </Col>
                          <Col
                            lg={3}
                            md={6}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label={`GST Amount (${serviceRecord?.taxPercentage ?? 18}%)`}
                              value={Number(
                                serviceRecord?.serviceTaxTotal || 0,
                              ).toFixed(2)}
                              disabled
                            />
                          </Col>
                          <Col
                            lg={3}
                            md={6}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Gross Total (incl. GST)"
                              value={Number(
                                serviceRecord?.serviceGrossTotal || 0,
                              ).toFixed(2)}
                              disabled
                            />
                          </Col>
                        </Row>

                        <ServicePaymentSection
                          serviceRecord={serviceRecord}
                          onRefresh={() =>
                            fetchServiceRecord(serviceRecord.erfServiceNumber)
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ACC 5 — SE Completion Update */}
              {isSE && (
                <div
                  className="accordion"
                  style={{
                    borderRadius: 10,
                    overflow: "hidden",
                    marginBottom: 12,
                  }}
                >
                  <div className="accordion-item">
                    <AccordionHeader
                      iconLabel="SU"
                      title="SE Completion Update"
                      subtitle={
                        isServiceCompleted
                          ? "Service Completed"
                          : !seCanAct
                            ? "Waiting for Store to confirm replacement barcodes"
                            : "Update service completion status"
                      }
                      isOpen={isUpdateOpen}
                      onToggle={() => setIsUpdateOpen((p) => !p)}
                    />
                    {isUpdateOpen && (
                      <div className="accordion-body">
                        {/* Case 1: Already completed — read-only summary */}
                        {isServiceCompleted ? (
                          <div className="p-3">
                            <div className="alert alert-success d-flex gap-3 align-items-start mb-0">
                              <i className="bi bi-check-circle-fill fs-3 flex-shrink-0" />
                              <div>
                                <h6 className="fw-bold mb-2">
                                  Service Completed
                                </h6>
                                <div className="row g-2">
                                  <div className="col-md-4">
                                    <small className="text-muted d-block">
                                      Completed By
                                    </small>
                                    <span className="fw-semibold">
                                      {serviceRecord.completedByName || "—"}
                                    </span>
                                  </div>
                                  <div className="col-md-4">
                                    <small className="text-muted d-block">
                                      Completed At
                                    </small>
                                    <span className="fw-semibold">
                                      {serviceRecord.completedAt
                                        ? new Date(
                                            serviceRecord.completedAt,
                                          ).toLocaleString("en-IN", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                        : "—"}
                                    </span>
                                  </div>
                                  {serviceRecord.completionDate && (
                                    <div className="col-md-4">
                                      <small className="text-muted d-block">
                                        Completion Date
                                      </small>
                                      <span className="fw-semibold">
                                        {serviceRecord.completionDate}
                                      </span>
                                    </div>
                                  )}
                                  {serviceRecord.completionNotes && (
                                    <div className="col-12 mt-1">
                                      <small className="text-muted d-block">
                                        Notes
                                      </small>
                                      <span>
                                        {serviceRecord.completionNotes}
                                      </span>
                                    </div>
                                  )}
                                  {serviceRecord.completionAttachmentId && (
                                    <div className="col-12 mt-2 d-flex align-items-center gap-2">
                                      <small className="text-muted">
                                        Completion Document:
                                      </small>
                                      <img
                                        src="assets/img/newIcons/attached-file.png"
                                        className="attachmentIcon"
                                        alt="view document"
                                        style={{ cursor: "pointer", width: 26 }}
                                        title="View completion document"
                                        onClick={() =>
                                          setFileViewModal({
                                            show: true,
                                            id: serviceRecord.completionAttachmentId,
                                          })
                                        }
                                      />
                                      <span
                                        className="text-primary small"
                                        style={{ cursor: "pointer" }}
                                        onClick={() =>
                                          setFileViewModal({
                                            show: true,
                                            id: serviceRecord.completionAttachmentId,
                                          })
                                        }
                                      >
                                        View Document
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : !seCanAct ? (
                          /* Case 2: Store hasn't confirmed barcodes */
                          <div className="p-4 text-center text-muted">
                            <i className="bi bi-lock fs-3 d-block mb-2" />
                            Store must assign and confirm all replacement
                            barcodes before you can update the service status.
                          </div>
                        ) : (
                          /* Case 3: Active form */
                          <Row>
                            <Col
                              lg={3}
                              md={6}
                              className="commTopButtonRightLeftPadding"
                            >
                              <label className="form-label">Status</label>
                              <select
                                className="form-control inputBox"
                                value={seUpdateData.completionStatus}
                                onChange={(e) =>
                                  setSeUpdateData((p) => ({
                                    ...p,
                                    completionStatus: e.target.value,
                                  }))
                                }
                              >
                                <option value={SERVICE_STATUS.IN_PROGRESS}>
                                  In Progress
                                </option>
                                <option value={SERVICE_STATUS.COMPLETED}>
                                  Completed
                                </option>
                                <option value={SERVICE_STATUS.CANCELLED}>
                                  Cancelled
                                </option>
                              </select>
                            </Col>

                            <Col
                              lg={3}
                              md={6}
                              className="commTopButtonRightLeftPadding"
                            >
                              <InputField
                                className="inputBox"
                                label="Completion Date"
                                type="date"
                                name="completionDate"
                                value={seUpdateData.completionDate}
                                onChange={(e) =>
                                  setSeUpdateData((p) => ({
                                    ...p,
                                    completionDate: e.target.value,
                                  }))
                                }
                              />
                            </Col>

                            <Col
                              lg={12}
                              md={12}
                              className="commTopButtonRightLeftPadding mt-2"
                            >
                              <TextAreaField
                                className="inputBox"
                                label="Completion Notes"
                                value={seUpdateData.completionNotes}
                                onChange={(e) =>
                                  setSeUpdateData((p) => ({
                                    ...p,
                                    completionNotes: e.target.value,
                                  }))
                                }
                                rows={2}
                              />
                            </Col>

                            {seUpdateData.completionStatus ===
                              SERVICE_STATUS.COMPLETED && (
                              <Col
                                lg={6}
                                md={12}
                                className="commTopButtonRightLeftPadding mt-2"
                              >
                                <FileUploadComponentForHB
                                  id="completionDocumentModal"
                                  name="completionDocumentModal"
                                  label={
                                    <span>
                                      Completion Document{" "}
                                      <span className="text-danger">*</span>
                                    </span>
                                  }
                                  allowedTypes={[
                                    "image/png",
                                    "image/jpeg",
                                    "application/pdf",
                                  ]}
                                  multiple={false}
                                  onSuccessUpload={(uploadData) =>
                                    setCompletionAttachmentId(
                                      uploadData.attachmentId,
                                    )
                                  }
                                  value={completionAttachmentId}
                                />
                                {completionAttachmentId && (
                                  <img
                                    src="assets/img/newIcons/attached-file.png"
                                    className="attachmentIcon"
                                    alt="attachment"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      setFileViewModal({
                                        show: true,
                                        id: completionAttachmentId,
                                      })
                                    }
                                  />
                                )}
                                {!completionAttachmentId && (
                                  <small className="text-danger">
                                    Document is required to mark as Completed
                                  </small>
                                )}
                              </Col>
                            )}
                            <Row className="commTopButtonRightLeftPadding mt-2 mb-2">
                              <Col
                                xs={6}
                                md={6}
                                lg={3}
                                className="mt-3 text-end"
                              >
                                <button
                                  className="btnTable"
                                  disabled={loading}
                                  onClick={handleSeCompletionUpdate}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <i className="bi bi-check-circle" />
                                  {loading
                                    ? "Updating..."
                                    : "Update Completion"}
                                </button>
                              </Col>
                            </Row>
                          </Row>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* ACC 5: Tally Status */}
              <ServiceTallyStatusSection
                erfServiceNumber={serviceRecord?.erfServiceNumber}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light pt-2 pb-2">
          <Row className="w-100 align-items-center">
            <Col xs={12} md={12} lg={12} className="text-end">
              <button
                type="button"
                className="btn btn-danger rounded-md"
                onClick={onHide}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <i className="bi bi-x-circle" />
                Close
              </button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      <FileViewerModalForHB
        show={fileViewModal.show}
        onHide={() => setFileViewModal({ show: false, id: null })}
        fileid={fileViewModal.id}
        showDelete={false}
      />

      {/* Stock-in confirmation sub-modal */}
      <StockInConfirmModal
        show={!!stockInModal}
        onHide={() => setStockInModal(null)}
        item={stockInModal?.item}
        onConfirm={handleStockInConfirm}
      />
    </>
  );
};

export default ViewServiceDetailsModal;
